import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useContent } from "../content/ContentContext";

export const DEFAULT_CARD_COLORS = {
  default: "#FAF9F6",
  hover: "#F5F0FC",
  selected: "#EEE8FA",
  border: "#7D56AA",
  recommendedBg: "#DED0F3",
  heading: "#563B78",
  body: "#28272B",
  check: "#7D56AA",
};

export function useCardColors() {
  const { content } = useContent();
  return { ...DEFAULT_CARD_COLORS, ...(content.site.cardColors || {}) };
}

/**
 * Card with lavender hover / selection effects. Clicking or tapping the card body (not a link
 * or button inside it) selects it; Enter/Space does the same from the keyboard. Selection is
 * owned by the parent so each group (services, packages) keeps one selected card.
 */
export default function SelectableCard({ id, selected, onSelect, recommended = false, className = "", testid, children }) {
  const [hover, setHover] = useState(false);
  const c = useCardColors();
  const lit = hover || selected;
  const background = recommended ? c.recommendedBg : selected ? c.selected : hover ? c.hover : c.default;
  const borderColor = recommended || lit ? c.border : "#DED5EC";
  const boxShadow = recommended
    ? lit ? "0 18px 40px -16px rgba(86,59,120,0.5)" : "0 12px 32px -18px rgba(86,59,120,0.38)"
    : lit ? "0 12px 30px -18px rgba(86,59,120,0.35)" : "none";

  const select = () => onSelect && onSelect(id);
  const onClick = (e) => { if (!e.target.closest("a,button,select,input")) select(); };
  const onKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); select(); }
  };

  return (
    <article
      id={id}
      data-testid={testid}
      data-selected={selected ? "true" : "false"}
      data-recommended={recommended ? "true" : undefined}
      aria-current={selected ? "true" : undefined}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{ background, borderColor, boxShadow, borderWidth: recommended ? 2 : 1, color: c.body }}
      className={`relative cursor-pointer rounded-2xl border transition-[background-color,border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${className}`}
    >
      {selected && (
        <span
          className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: c.heading }}
          data-testid={`${testid}-selected-indicator`}
        >
          <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.5} />
          Selected
        </span>
      )}
      {children}
    </article>
  );
}
